import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Linkedin, Facebook, Instagram, Youtube, Mail, MessageCircle, Phone, MapPin, ArrowUpRight, Globe2, Loader2 } from "lucide-react";
import { CONTACT } from "../data/site";
import { api } from "../lib/api";

const COLS = [
  { title: "Quick Links", links: [["About Us", "/about"], ["Services", "/services"], ["Industries", "/industries"], ["Request a Quote", "/quote"]] },
  { title: "Engineering", links: [["Commodities", "/commodities"], ["Supplier Registration", "/supplier-registration"], ["Buyer Registration", "/buyer-registration"], ["Buyer RFQ", "/request-quote"]] },
  { title: "Legal", links: [["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"], ["Contact", "/contact"], ["Admin", "/login"]] },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const wa = `https://wa.me/${CONTACT.phoneRaw}`;

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    [Linkedin, CONTACT.linkedin], [Facebook, CONTACT.facebook], [Instagram, CONTACT.instagram],
    [Youtube, CONTACT.youtube], [Mail, `mailto:${CONTACT.email}`], [MessageCircle, wa],
  ];

  return (
    <footer data-testid="footer" className="bg-navy text-white">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-6">
              <span className="h-10 w-10 bg-brand-orange grid place-items-center">
                <Globe2 size={22} className="text-white" />
              </span>
              <span className="font-display font-black text-xl">SourceX Global</span>
            </div>
            <p className="text-white/60 max-w-sm leading-relaxed mb-6">
              Connecting global buyers with trusted Indian manufacturers of engineering products —
              from RFQ to export delivery.
            </p>
            <div className="space-y-3 text-sm">
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-white/70 hover:text-brand-orange transition-colors">
                <Mail size={16} className="text-brand-orange" /> {CONTACT.email}
              </a>
              <a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/70 hover:text-brand-orange transition-colors">
                <MessageCircle size={16} className="text-brand-orange" /> {CONTACT.phone}
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 text-white/70 hover:text-brand-orange transition-colors">
                <Phone size={16} className="text-brand-orange" /> {CONTACT.phone}
              </a>
              <p className="flex items-center gap-3 text-white/70">
                <MapPin size={16} className="text-brand-orange" /> {CONTACT.address}
              </p>
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="overline mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map(([label, to]) => (
                  <li key={label + to}>
                    <Link to={to} className="text-white/70 hover:text-white transition-colors inline-flex items-center gap-1 group text-sm">
                      {label}
                      <ArrowUpRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="overline mb-5">Newsletter</h4>
            <p className="text-white/60 text-sm mb-4">Get sourcing insights & supplier updates.</p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="space-y-2">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder:text-white/40 rounded-none focus:outline-none focus:border-brand-orange"
              />
              <button data-testid="newsletter-submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-white py-2.5 font-mono uppercase text-[10px] tracking-[0.2em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
                {loading ? <Loader2 size={14} className="animate-spin" /> : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-white/50 font-mono uppercase tracking-[0.15em] order-2 md:order-1">
            © 2026 SourceX Global. All Rights Reserved.
          </p>
          <div className="flex gap-3 order-1 md:order-2">
            {socials.map(([Icon, href], i) => (
              <a key={i} href={href} target="_blank" rel="noreferrer"
                data-testid={`social-${i}`}
                className="h-10 w-10 border border-white/20 grid place-items-center hover:bg-brand-orange hover:border-brand-orange transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
